import {
  IVizDTO,
  VizDTO,
  VizType,
  useGroopitApiContext,
  useGroupContext,
} from "groopit-js";
import React, { useEffect } from "react";
import { LeaderboardVizWrapper } from "./LeaderboardViz";
import { MapVizPreview } from "./MapViz";
import { TimelineWrapper } from "./TimelineWrapper";
import { TagCountVizWrapper } from "./TagCountViz";
import { GoalVizPreview } from "./GoalViz";
import { CounterVizWrapper } from "./CounterViz";
import useLatestRef from "../react/react-use/useLatestRef";
import { CircularProgress } from "@material-ui/core";

interface VisualizationsContainerProps {
  isEditable: boolean;
  visualizations?: VizDTO[];
}

export const VisualizationsContainer = (
  props: VisualizationsContainerProps,
): JSX.Element => {
  const { isEditable } = { ...props };
  const { group, owner } = useGroupContext();
  const [visualizations, setVisualizations] = React.useState<
    VizDTO[] | undefined
  >(props.visualizations);
  const api = useLatestRef(useGroopitApiContext());

  useEffect(() => {
    if (api?.current && !visualizations) {
      api.current.groop
        .getViews(group.id)
        .then((visualizations) => {
          setVisualizations(visualizations);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [api, group.id, visualizations]);

  const getColumns = (viz: VizDTO) => {
    switch (viz.vizType) {
      case VizType.Map:
        return 2;
      default:
        return 1;
    }
  };

  const getRows = (viz: VizDTO) => {
    switch (viz.vizType) {
      case VizType.Leaderboard:
      case VizType.TagCount:
      case VizType.ChoiceCount:
      case VizType.Map:
        return 2;
      default:
        return 1;
    }
  };

  const VizSelector = (viz: VizDTO): JSX.Element => {
    switch (viz.vizType) {
      case VizType.Leaderboard:
        return (
          <LeaderboardVizWrapper {...viz} supportsFeedDateFilters={true} />
        );
      case VizType.Counter:
      case VizType.CounterWithTotal:
        return <CounterVizWrapper {...viz} />;
      case VizType.Goal:
        return <GoalVizPreview {...viz} />;
      case VizType.Timeline:
        return <TimelineWrapper {...viz} />;
      case VizType.ChoiceCount:
      case VizType.TagCount:
        return <TagCountVizWrapper {...viz} supportsFeedDateFilters={true} />;
      case VizType.Map:
        return <MapVizPreview {...viz} />;
      default:
        throw "Unknown viz type " + viz.vizType;
    }
  };

  const updateVizOrder = (visualizations: IVizDTO[]) => {
    const formData = new FormData();
    visualizations.forEach((v) => {
      if (v.id) {
        formData.append("viewOrder[]", v.id?.toString());
      }
    });
    fetch(`/Groop/ViewOrder/${group.id}?`, {
      method: "POST",
      body: formData,
    });
  };

  const moveVizUp = (viz: VizDTO) => {
    const index = visualizations?.findIndex((v) => v.id === viz.id);
    if (visualizations && index !== undefined && index > 0) {
      const newVisualizations = [...visualizations];
      const temp = newVisualizations[index - 1];
      newVisualizations[index - 1] = newVisualizations[index];
      newVisualizations[index] = temp;
      setVisualizations(newVisualizations);
      updateVizOrder(newVisualizations);
    }
  };

  const moveVizDown = (viz: VizDTO) => {
    const index = visualizations?.findIndex((v) => v.id === viz.id);
    if (
      visualizations &&
      index !== undefined &&
      index < visualizations.length
    ) {
      const newVisualizations = [...visualizations];
      const temp = newVisualizations[index + 1];
      newVisualizations[index + 1] = newVisualizations[index];
      newVisualizations[index] = temp;
      setVisualizations(newVisualizations);
      updateVizOrder(newVisualizations);
    }
  };

  return visualizations ? (
    <div
      className="grouppage-content wide feed md:grid"
      style={{ gridAutoRows: "400px" }}
    >
      {visualizations?.map((viz) => (
        <div
          key={viz.id}
          className="viewwrapper"
          data-id={`${viz.id}`}
          data-group={group.id}
          style={{
            gridColumn: `span ${getColumns(viz)}`,
            gridRow: `span ${getRows(viz)}`,
          }}
        >
          <div className="flex justify-between border-b">
            <div className="text-display-normal text-color-secondary">
              {viz.title}
            </div>
            {isEditable && owner && (
              <div className="dropdown">
                <button
                  className="btn-none dropdown-toggle"
                  type="button"
                  data-toggle="dropdown"
                >
                  <span className="glyphicon glyphicon-option-vertical text-color-hint"></span>
                </button>
                <ul className="dropdown-menu dropdown-menu-right">
                  <li>
                    <a href={`/Groop/EditDataView/${viz.id}`}>
                      <img
                        src="/Content/Images/edit.png"
                        className="menu-icon"
                      />
                      Edit
                    </a>
                  </li>
                  <li>
                    <form
                      method="post"
                      action={`/Groop/DeleteDataView/${viz.id}`}
                    >
                      <button className="btn-link" type="submit">
                        <img
                          src="/Content/Images/delete.png"
                          className="menu-icon"
                        />
                        Delete
                      </button>
                    </form>
                  </li>
                  <li className="viewmove up">
                    <button
                      className="btn-link"
                      type="button"
                      onClick={() => moveVizUp(viz)}
                    >
                      <img
                        src="/Content/Images/arrow.png"
                        className="menu-icon"
                        style={{ transform: "rotate(-90deg)" }}
                      />
                      Move Up
                    </button>
                  </li>
                  <li className="viewmove down">
                    <button
                      className="btn-link"
                      type="button"
                      onClick={() => moveVizDown(viz)}
                    >
                      <img
                        src="/Content/Images/arrow.png"
                        className="menu-icon"
                        style={{ transform: "rotate(90deg)" }}
                      />
                      Move Down
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
          {VizSelector(viz)}
          <div className="pb-8 border-t">{viz.description}</div>
        </div>
      ))}
    </div>
  ) : (
    <div className="text-center mt-8">
      <CircularProgress />
    </div>
  );
};
